// Settings that only turn on after a yes in a warning. The settings store (settings-store.js) keeps
// each of these off until this browser has said yes, so every way of turning one on (a switch, the
// extensions menu, the toolbar, the command palette, the island, a look, a backup file) ends up at
// the same warning. Turning it off forgets the yes, so turning it on again asks again.
//
// The yes lives under its own key, which backups and cloud sync never copy (backup.js NO_SYNC_KEYS),
// so a restored backup can't carry it to another browser.

export const CONSENT_KEY = "revault:consent";

export const WARNINGS = {
	extVencord: {
		name: "Vencord (beta)",
		title: "Turn on Vencord (beta)?",
		lines: [
			"Discord's rules don't allow modified clients. Using Vencord could get your Discord account limited or banned.",
			"While it's on, Vencord runs inside Discord with full access to your account and messages.",
			"This is the official open-source Vencord build, with a small Re:Vault host. Re:Vault checks it and serves it itself. It is never downloaded live.",
			"You can turn it off any time.",
		],
		ok: "Turn on",
		cancel: "Cancel",
	},
};

export const needsConsent = (key) => Object.prototype.hasOwnProperty.call(WARNINGS, key);

function read() {
	try {
		const o = JSON.parse(localStorage.getItem(CONSENT_KEY) || "{}");
		return o && typeof o === "object" && !Array.isArray(o) ? o : {};
	} catch { return {}; }
}

export const hasConsent = (key) => needsConsent(key) && read()[key] === true;

export function setConsent(key, on) {
	if (!needsConsent(key)) return;
	const o = read();
	if ((o[key] === true) === !!on) return;
	if (on) o[key] = true; else delete o[key];
	try {
		if (Object.keys(o).length) localStorage.setItem(CONSENT_KEY, JSON.stringify(o));
		else localStorage.removeItem(CONSENT_KEY);
	} catch { /* storage off: it simply stays off */ }
}

// The page that can show the warning registers how (consent-dialog.js): ask(key) -> Promise<boolean>.
// A page that never registers can't turn these on at all.
let asker = null;
export function setConsentAsker(fn) { asker = typeof fn === "function" ? fn : null; }

// One warning at a time per setting, however many places asked.
const pending = new Map();
export function askConsent(key) {
	if (!needsConsent(key) || !asker) return Promise.resolve(false);
	if (pending.has(key)) return pending.get(key);
	const p = Promise.resolve()
		.then(() => asker(key, WARNINGS[key]))
		.then((ok) => ok === true, () => false)
		.finally(() => pending.delete(key));
	pending.set(key, p);
	return p;
}

// A settings object from a backup file or a cloud backup: anything here that needs a yes is turned
// off. Returns the names of what was left off, for the "left off" message.
export function leaveOff(settings) {
	const names = [];
	if (!settings || typeof settings !== "object") return names;
	for (const key of Object.keys(WARNINGS)) {
		if (settings[key] === true) { settings[key] = false; names.push(WARNINGS[key].name); }
	}
	return names;
}

export function leftOffMessage(names) {
	if (!names || !names.length) return "";
	const list = names.length === 1 ? names[0] : names.slice(0, -1).join(", ") + " and " + names[names.length - 1];
	return list + (names.length === 1 ? " was" : " were") + " on in that backup and " + (names.length === 1 ? "is" : "are") + " left off here. You can turn " + (names.length === 1 ? "it" : "them") + " on in Settings, Extensions.";
}
